import { Button } from '@payaca/ambrosia';
import { FunctionComponent, type JSX } from 'react';

import {
  getCustomerByDealId,
  getDeal,
  getInvoice,
  getInvoiceLinesByInvoiceId,
  getInvoicesByDealId,
  getJobLineItemsByInvoiceId,
} from '@/utils/stateAccessors';

import { useSelector } from '@/api/state';
import ClientDocumentLoading from '@payaca/components/clientDocument/ClientDocumentLoading';
import InvoiceDocumentComponent from '@payaca/components/invoiceDocument/InvoiceDocument';

import { getDocumentSenderContextFromAccount } from '@payaca/helpers/documentSenderContextHelper';
import { useUploads } from '@payaca/store/hooks/appState';
import { ReadableInvoiceStatus } from '@payaca/types/invoiceTypes';

type Props = {
  invoiceId: number;
  isPreview?: boolean;
};

const InvoiceDocument: FunctionComponent<Props> = ({
  invoiceId,
  isPreview = false,
}: Props): JSX.Element | null => {
  const invoice = useSelector((state) => {
    return getInvoice(state, invoiceId);
  });

  const attachmentUploads = useUploads(invoice?.attachmentUploadIds);

  const deal = useSelector((state) => {
    return invoice ? getDeal(state, invoice.dealId) : undefined;
  });

  const customer = useSelector((state) => {
    return invoice ? getCustomerByDealId(state, invoice.dealId) : undefined;
  });

  const invoices = useSelector((state) =>
    invoice ? getInvoicesByDealId(state, invoice.dealId) : undefined
  );

  const jobLineItems = useSelector((state) => {
    return getJobLineItemsByInvoiceId(state, invoiceId);
  });

  const invoiceLines = useSelector((state) => {
    return getInvoiceLinesByInvoiceId(state, invoiceId);
  });

  const account = useSelector(
    (state: any) => state.users.myProfile.accounts[0]
  );

  if (!deal || !customer || !invoices?.length) return <ClientDocumentLoading />;

  return (
    <InvoiceDocumentComponent
      isPreview={isPreview}
      invoices={invoices}
      invoiceId={invoiceId}
      dealTotals={deal}
      siteAddresses={deal.siteAddresses || []}
      isStraightToInvoice={deal.isStraightToInvoice}
      customer={customer}
      jobLineItems={jobLineItems}
      invoiceLines={invoiceLines}
      documentSenderContext={getDocumentSenderContextFromAccount(account)}
      hideItemPrices={deal.hideItemPrices}
      attachments={attachmentUploads}
      paymentMethodConfig={{
        allowStripePayment: deal.allowStripePayment,
        allowBankTransferPayment: deal.allowBankTransferPayment,
        bankTransferPaymentSettings: {
          accountNumber: account.accountNumberRaw || '',
          accountName: account.accountNameRaw || '',
          sortCode: account.sortCodeRaw || '',
        },
      }}
      CTA={
        invoice?.readableStatus !== ReadableInvoiceStatus.PAID &&
        invoice?.readableStatus !== ReadableInvoiceStatus.VOID ? (
          <>
            <hr className="hr my-2" />

            <Button
              className="mt-2 w-full print:hidden"
              colour={'job-brand'}
              disabled
            >
              Make a payment
            </Button>
          </>
        ) : null
      }
    />
  );
};

export default InvoiceDocument;
